import React, { useMemo, useState } from 'react';

/**
 * TradesTable - Displays a table of backtest trades with charges breakdown tooltip
 */
const TradesTable = ({ trades, maxHeight = 400 }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'trade_id', direction: 'asc' });
    const [expandedRow, setExpandedRow] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        });
    };

    const formatCurrency = (value, compact = false) => {
        if (value === null || value === undefined) return '₹0';
        if (compact) {
            return '₹' + Number(value).toFixed(2);
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency', currency: 'INR',
            minimumFractionDigits: 2, maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '—';
        const pct = value * 100;
        const sign = pct >= 0 ? '+' : '';
        return `${sign}${pct.toFixed(2)}%`;
    };

    const sortedTrades = useMemo(() => {
        if (!trades || trades.length === 0) return [];
        const sorted = [...trades].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (sortConfig.key.includes('time')) {
                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
            }
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [trades, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortIndicator = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <span className="opacity-20"> ⇅</span>;
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    const hasCharges = trades && trades.some(t => t.charges);

    if (!trades || trades.length === 0) {
        return (
            <div className="flex items-center justify-center text-base-content/60 h-32">
                No trades available
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold">Trades ({trades.length})</h4>
                {hasCharges && (
                    <span className="text-xs text-base-content/50 italic">Click a row to see charge breakdown</span>
                )}
            </div>
            <div className="overflow-x-auto" style={{ maxHeight }}>
                <table className="table table-xs table-zebra w-full">
                    <thead className="sticky top-0 bg-base-200 z-10">
                        <tr>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('trade_id')}>#<SortIndicator columnKey="trade_id" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('side')}>Side<SortIndicator columnKey="side" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('entry_time')}>Entry<SortIndicator columnKey="entry_time" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('entry_price')}>Entry ₹<SortIndicator columnKey="entry_price" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('exit_time')}>Exit<SortIndicator columnKey="exit_time" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('exit_price')}>Exit ₹<SortIndicator columnKey="exit_price" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('qty')}>Qty<SortIndicator columnKey="qty" /></th>
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('pnl')}>Gross P&L<SortIndicator columnKey="pnl" /></th>
                            {hasCharges && (
                                <th className="cursor-pointer hover:bg-base-300 text-warning" onClick={() => handleSort('net_pnl')}>
                                    Charges<SortIndicator columnKey="net_pnl" />
                                </th>
                            )}
                            {hasCharges && (
                                <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('net_pnl')}>
                                    Net P&L<SortIndicator columnKey="net_pnl" />
                                </th>
                            )}
                            <th className="cursor-pointer hover:bg-base-300" onClick={() => handleSort('return')}>Return<SortIndicator columnKey="return" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTrades.map((trade) => {
                            const isProfitable = trade.pnl >= 0;
                            const isNetProfitable = trade.net_pnl !== null ? trade.net_pnl >= 0 : isProfitable;
                            const isBuy = trade.side === 'BUY';
                            const isExpanded = expandedRow === trade.trade_id;
                            const charges = trade.charges;

                            return (
                                <React.Fragment key={trade.trade_id}>
                                    <tr
                                        className={`hover ${hasCharges ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-base-200' : ''}`}
                                        onClick={() => hasCharges && setExpandedRow(isExpanded ? null : trade.trade_id)}
                                    >
                                        <td className="font-mono">{trade.trade_id}</td>
                                        <td>
                                            <span className={`badge badge-sm ${isBuy ? 'badge-success' : 'badge-error'}`}>
                                                {trade.side}
                                            </span>
                                        </td>
                                        <td className="text-xs">{formatDate(trade.entry_time)}</td>
                                        <td className="font-mono">{formatCurrency(trade.entry_price)}</td>
                                        <td className="text-xs">{formatDate(trade.exit_time)}</td>
                                        <td className="font-mono">{formatCurrency(trade.exit_price)}</td>
                                        <td className="text-center">{trade.qty}</td>
                                        <td className={`font-mono font-semibold ${isProfitable ? 'text-success' : 'text-error'}`}>
                                            {formatCurrency(trade.pnl)}
                                        </td>
                                        {hasCharges && (
                                            <td className="font-mono text-warning">
                                                {charges ? `−${formatCurrency(charges.total)}` : '—'}
                                            </td>
                                        )}
                                        {hasCharges && (
                                            <td className={`font-mono font-bold ${isNetProfitable ? 'text-success' : 'text-error'}`}>
                                                {trade.net_pnl !== null ? formatCurrency(trade.net_pnl) : '—'}
                                            </td>
                                        )}
                                        <td className={`font-mono ${isProfitable ? 'text-success' : 'text-error'}`}>
                                            {formatPercent(trade.return)}
                                        </td>
                                    </tr>
                                    {/* Expanded charges breakdown row */}
                                    {isExpanded && charges && (
                                        <tr className="bg-base-200">
                                            <td colSpan={hasCharges ? 11 : 9} className="py-2 px-4">
                                                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-base-content/70">
                                                    <span className="font-semibold text-base-content/90 mr-2">Charge Breakdown:</span>
                                                    <span>Brokerage: <span className="font-mono text-warning">{formatCurrency(charges.brokerage)}</span></span>
                                                    <span>STT: <span className="font-mono text-warning">{formatCurrency(charges.stt)}</span></span>
                                                    <span>GST: <span className="font-mono text-warning">{formatCurrency(charges.gst)}</span></span>
                                                    <span>Stamp: <span className="font-mono text-warning">{formatCurrency(charges.stampDuty)}</span></span>
                                                    <span>Txn: <span className="font-mono text-warning">{formatCurrency(charges.transactionCharges)}</span></span>
                                                    <span>SEBI: <span className="font-mono text-warning">{formatCurrency(charges.sebiTurnoverFee)}</span></span>
                                                    {charges.dpCharges > 0 && <span>DP: <span className="font-mono text-warning">{formatCurrency(charges.dpCharges)}</span></span>}
                                                    <span className="font-semibold">Total: <span className="font-mono text-error">{formatCurrency(charges.total)}</span></span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TradesTable;
